import React from 'react';
import { Info, AlertTriangle } from 'lucide-react';

export default function VolumeAssumptionsBlock({
  volumeAssumptions,
  setVolumeAssumptions,
  totalPaymentsAutoCalculated,
  handleTotalPaymentsChange,
  resetTotalPaymentsToAuto,
  calculateVolumeTotal
}) {
  return (
    <div className="card-modern p-0 overflow-hidden">
      <div className="bg-gradient-to-r from-sky-600 to-sky-700 px-6 py-4 relative">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        <h2 className="text-lg font-bold text-white relative z-10">
          BLOCK 2: VOLUME ASSUMPTIONS <span className="text-accent text-sm ml-2">(All fields required *)</span>
        </h2>
      </div>
      <div className="p-6 grid grid-cols-2 gap-5">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-slate-700">
              Total Payments <span className="text-blue-600 font-bold">($000s)</span> <span className="text-red-600">*</span>
            </label>
            {!totalPaymentsAutoCalculated && (
              <button
                type="button"
                onClick={resetTotalPaymentsToAuto}
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                title="Reset to auto-calculate"
              >
                Reset to Auto
              </button>
            )}
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold text-xs">$</span>
            <input
              type="text"
              value={volumeAssumptions.totalPayments ? volumeAssumptions.totalPayments.toLocaleString('en-US') : ''}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/,/g, '');
                handleTotalPaymentsChange(parseFloat(numericValue) || 0);
              }}
              onFocus={(e) => setTimeout(() => e.target.select(), 0)}
              onClick={(e) => e.target.select()}
              className={`input-field !pl-7 ${totalPaymentsAutoCalculated ? 'bg-green-50 border-green-300' : 'bg-yellow-50'}`}
              placeholder="e.g., 1,000 (= $1,000,000)"
            />
            {totalPaymentsAutoCalculated && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-600 font-medium">
                Auto
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {totalPaymentsAutoCalculated 
              ? '✓ Auto-calculated from transactions & distribution' 
              : '⚠️ Manual override active'}
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Total Transactions <span className="text-blue-600 font-bold">(Count)</span> <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={volumeAssumptions.totalTransactions ? volumeAssumptions.totalTransactions.toLocaleString('en-US') : ''}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/,/g, '');
              setVolumeAssumptions({ ...volumeAssumptions, totalTransactions: parseInt(numericValue) || 0 });
            }}
            onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
            className="input-field bg-yellow-50"
            placeholder="e.g., 50,000"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            ACH Volume <span className="text-blue-600 font-bold">(%)</span> <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              value={volumeAssumptions.achPercent || ''}
              onChange={(e) => setVolumeAssumptions({ ...volumeAssumptions, achPercent: parseInt(e.target.value) || 0 })}
              onFocus={(e) => setTimeout(() => e.target.select(), 0)}
              onClick={(e) => e.target.select()}
              className="input-field bg-yellow-50 pr-10"
              step="1"
              max="100"
              placeholder="e.g., 15"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            CC Volume <span className="text-slate-500 text-xs">(V, MC, D)</span> <span className="text-blue-600 font-bold">(%)</span> <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              value={volumeAssumptions.ccPercent || ''}
              onChange={(e) => setVolumeAssumptions({ ...volumeAssumptions, ccPercent: parseInt(e.target.value) || 0 })}
              onFocus={(e) => setTimeout(() => e.target.select(), 0)}
              onClick={(e) => e.target.select()}
              className="input-field bg-yellow-50 pr-10"
              step="1"
              max="100"
              placeholder="e.g., 62"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            AMEX Volume <span className="text-blue-600 font-bold">(%)</span> <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              value={volumeAssumptions.amexPercent || ''}
              onChange={(e) => setVolumeAssumptions({ ...volumeAssumptions, amexPercent: parseInt(e.target.value) || 0 })}
              onFocus={(e) => setTimeout(() => e.target.select(), 0)}
              onClick={(e) => e.target.select()}
              className="input-field bg-yellow-50 pr-10"
              step="1"
              max="100"
              placeholder="e.g., 9"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            DEBIT Volume <span className="text-blue-600 font-bold">(%)</span> <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              value={volumeAssumptions.debitPercent || ''}
              onChange={(e) => setVolumeAssumptions({ ...volumeAssumptions, debitPercent: parseInt(e.target.value) || 0 })}
              onFocus={(e) => setTimeout(() => e.target.select(), 0)}
              onClick={(e) => e.target.select()}
              className="input-field bg-yellow-50 pr-10"
              step="1"
              max="100"
              placeholder="e.g., 14"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>
          </div>
        </div>
        
        <div className="col-span-2">
          <label className="block text-sm font-medium text-slate-700 mb-2">Total</label>
          <input
            type="text"
            value={`${calculateVolumeTotal()}%`}
            disabled
            className={`input-field font-bold ${Math.abs(parseFloat(calculateVolumeTotal()) - 100) < 0.01 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
          />
        </div>
      </div>
      
      {/* Smart Guide: Total Payments vs Transaction Count Relationship */}
      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-indigo-500">
        <div className="flex gap-3">
          <Info className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-bold text-indigo-900 mb-2">💡 Smart Guide: How Total Payments Works</h4>
            <div className="text-xs text-indigo-800 space-y-2">
              <p className="leading-relaxed">
                <strong>Order of Input Matters:</strong>
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>If you edit Total Transactions first:</strong> Total Payments will <strong>auto-calculate</strong> based on the transaction distribution percentages (Block 5). This is the recommended approach.
                </li>
                <li>
                  <strong>If you edit Total Payments first:</strong> It becomes a <strong>manual override</strong> and will NOT change when you modify Transaction Count. You'll need to click "Reset to Auto" to re-enable automatic calculation.
                </li>
              </ul>
              <div className="mt-2 pt-2 border-t border-indigo-200">
                <p className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span><strong>Tip:</strong> For most accurate calculations, enter Total Transactions first and let Total Payments auto-calculate!</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
